import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import AskDialog from '../../../components/common/dialog/AskDialog';
import { useNavigate } from 'react-router-dom';
import * as chanceApi from '../../../lib/api/chance';
import _ from 'lodash';
import { setChance } from '../../../modules/chanceModule/chance';
import { Box, Stepper, Step, StepLabel, Grid, Button } from '@mui/material';
import ChanceConfirmContainer from './ChanceConfirmContainer';
import ChanceNeedsAnanlysisContainer from './ChanceNeedsAnanlysisContainer';
import ChanceEstimateContainer from './ChanceEstimateContainer';
import ChanceOrderContainer from './ChanceOrderContainer';
import ChanceStepInfoContainer from '../info/ChanceStepInfoContainer';
import ChanceGuideContainer from '../info/ChanceGuideContainer';
import ChanceFailModal from '../../../components/chancemng/info/ChanceFailModal';
import useActionPermission from '../../../hooks/useActionPermission';

const steps = [
  { value: 0, label: '기회 확인', color: 'info' },
  { value: 1, label: '기회 분석', color: 'error' },
  { value: 2, label: '매출 견적', color: 'success' },
  { value: 3, label: '수주 품의', color: 'secondary' },
];

const ChanceStepperContainer = ({ activeStep, activeTap, handleTabChange }) => {
  const stepperRef = useRef();

  const dispatch = useDispatch();
  let navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  // 삭제 권한. 본인이 만든 것이거나 삭제 권한자여야 한다. 되돌릴 수 없는 작업이라 서버에서도
  // 다시 막는다(ksm_backend/ksm_frontend 포팅, 2026-10-19) — 예전엔 화면에서 권한목록(authTyp=
  // FORECAST_DELETE)만 보고 판단해서 "본인이 만든 것" 은 걸러낼 수 없었다.
  const [canDelete, setCanDelete] = useState(false);
  // 기회 복사/기회 실패 버튼 노출. 서버도 다시 막는다(ForecastController, bito 4단계 2026-10-20) —
  // 여긴 버튼만 숨긴다.
  const { allowed: canCopy } = useActionPermission('FORECAST_COPY');
  const { allowed: canFail } = useActionPermission('FORECAST_FAIL');
  const { enqueueSnackbar } = useSnackbar();
  const { chance, user } = useSelector(({ chanceModule, auth }) => ({
    chance: chanceModule.chance.chance,
    user: auth.user,
  }));

  useEffect(() => {

    // 영업이익 마이너스 권한
    const findIndex = _.findIndex(
      user?.privilegeList,
      (auth) => auth?.authTyp === 'NO_PERPROMANCE' && auth?.authYn === 'Y',
    );
    // console.log(findIndex, 'findIndex');
    if (findIndex !== -1) {
      setIsAuth(true);
    }
    // 삭제 권한은 서버에 물어본다(아래 effect) — 권한목록만 뒤지면 "권한자"는 알 수 있어도
    // "본인이 만든 것"은 판단할 수 없다.

  }, [user]);

  // 삭제 권한 조회. 본인이 만든 것이거나 삭제 권한자면 참이다.
  useEffect(() => {
    if (!chance?.prjUuid) {
      setCanDelete(false);
      return;
    }
    chanceApi.getChanceDeletePermission(chance.prjUuid)
      .then((res) => setCanDelete(Boolean(res?.data?.allowed)))
      // 못 읽으면 숨긴다. 되돌릴 수 없는 작업이라 열어두는 것보다 안전하다.
      .catch(() => setCanDelete(false));
  }, [chance?.prjUuid]);

  const [message, setMessage] = useState(
    '기본사항 등록이 완료되지 못했습니다.',
  );
  const [visible, setVisible] = useState(false);
  const onCancle = () => {
    setVisible(false);
  };
  const onConfirm = () => {
    setVisible(false);
  };

  const [failVisible, setFailVisible] = useState(false);
  const onFailCancle = () => {
    setFailVisible(false);
  };
  const onFailConfirm = (failTxt) => {
    //수주실패
    onFailProject(failTxt);
    setFailVisible(false);
  };

  const onFailProject = async (failTxt) => {
    let dataToSubmit = {
      prjUuid: chance.prjUuid,
      cstStatus: '수주실패',
      failTxt: failTxt,
      useYn: 'N',
    };

    try {
      const res = await chanceApi.editChance(dataToSubmit);
      if (res.data) {
        navigate('/chancemng');
        enqueueSnackbar(`프로젝트 종료 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const [copyVisible, setCopyVisible] = useState(false);
  const onCopyCancle = () => {
    setCopyVisible(false);
  };
  const onCopyConfirm = () => {
    //프로젝트 복사
    onCopyProject();
    setCopyVisible(false);
  };

  const onCopyProject = async () => {
    try {
      const res = await chanceApi.copyChance(chance.prjUuid);
      if (res.data) {
        navigate('/chancemng');
        enqueueSnackbar(`프로젝트 복사 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const [removeVisible, setRemoveVisible] = useState(false);
  const onRemoveCancle = () => {
    setRemoveVisible(false);
  };
  const onRemoveConfirm = () => {
    //프로젝트 삭제
    onRemoveProject();
    setRemoveVisible(false);
  };

  const onRemoveProject = async () => {
    try {
      const res = await chanceApi.removeChance(chance?.prjUuid);
      if (res.data) {
        navigate('/chancemng');
        enqueueSnackbar(`프로젝트 삭제 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const handleNext = async () => {
    //현재 단계에서 모든 상세가 입력되었는지 확인
    // 2026-10-20 - "원가산출이 확정되지 않았습니다" 오탐 수정(KSM 포팅): Redux의 chance.costYn이
    // 다른 탭/화면에서의 저장 이후로 최신화되지 않은 채로 남아있을 수 있어서(세부 저장 등 다른
    // API 응답으로 chance 전체가 교체되면서 costYn이 갈아치워지는 경우가 있음), 여기서 서버에
    // 한 번 더 물어 최신 costYn으로 검증한다. 원가확정 여부를 검사하는 1·2단계에서만 호출.
    let serverCostYn = chance?.costYn;
    if (activeStep === 1 || activeStep === 2) {
      try {
        const res = await chanceApi.getChance(chance.prjUuid);
        if (res.data.info) {
          dispatch(setChance(res.data.info));
          serverCostYn = res.data.info.costYn;
        }
      } catch (err) {
        // 조회 실패시 기존 Redux 상태로 진행(기존 동작 유지).
      }
    }

    const valid = checkValidation(serverCostYn);
    if (!valid) {
      setVisible(true);
      return;
    } else {
      let dataToSubmit = {
        prjUuid: chance.prjUuid,
      };

      if (Number(chance.cstGrade) === 1) {
        dataToSubmit.cstGrade = 2;
        dataToSubmit.cstStatus = '기회분석';
        updateChanceStatus(dataToSubmit);
      }

      if (Number(chance.cstGrade) === 2) {
        dataToSubmit.cstGrade = 3;
        dataToSubmit.cstStatus = '견적';
        updateChanceStatus(dataToSubmit);
      }

      if (Number(chance.cstGrade) === 3) {
        dataToSubmit.cstGrade = 4;
        dataToSubmit.cstStatus = '수주';
        updateOrderStatus(dataToSubmit);
      }
    }
  };

  const updateOrderStatus = async (dataToSubmit) => {
    try {
      const res = await chanceApi.getEstimateCount(chance.prjUuid);
      if (!res.data.count || res.data.count === 0) {
        setMessage('작성된 견적서가 없습니다. 견적서를 작성하세요');
        setVisible(true);
        return;
      } else {
        updateChanceStatus(dataToSubmit);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const updateChanceStatus = async (dataToSubmit) => {
    let moveTab = 1;
    if (dataToSubmit.cstGrade === 3) {
      moveTab = 3;
    } else if (dataToSubmit.cstGrade === 4) {
      moveTab = 6;
    }
    try {
      const res = await chanceApi.editChance(dataToSubmit);
      if (res.data) {
        dispatch(setChance(res.data.info));
        navigate(
          `/chancemng/${dataToSubmit.prjUuid}/${dataToSubmit.cstGrade}/${moveTab}`,
        );
        enqueueSnackbar(`[${dataToSubmit.cstStatus}] 상태 변경 성공`, {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const checkValidation = (serverCostYn) => {
    let valid = false;
    // handleNext가 넘겨준 서버 확인값을 우선 쓰고, 없으면(0단계 등 재조회 안 하는 경우) 기존
    // Redux 값으로 fallback - 2026-10-20 "원가산출이 확정되지 않았습니다" 오탐 수정(KSM 포팅).
    const costYnValue = serverCostYn || chance?.costYn;

    if (activeStep === 0) {
      valid = checkValid1();
    } else if (activeStep === 1) {
      valid = checkValid2(costYnValue);

      if (valid) {
        if (
          costYnValue === null ||
          costYnValue === undefined ||
          costYnValue === '' ||
          costYnValue === 'N'
        ) {
          valid = false;
          setMessage(
            '원가산출이 확정되지 않았습니다. 원가산출관리에서 확정하십시오',
          );
        }
      }
    } else if (activeStep === 2) {
      valid = checkValid3(costYnValue);

      if (valid) {
        if (
          costYnValue === null ||
          costYnValue === undefined ||
          costYnValue === '' ||
          costYnValue === 'N'
        ) {
          valid = false;
          setMessage(
            '원가산출이 확정되지 않았습니다. 원가산출관리에서 확정하십시오',
          );
        }
      }
    }

    return valid;
  };

  const checkValid1 = () => {
    let valid = true;
    if (
      chance.cstNm === null ||
      chance.cstNm === undefined ||
      chance.cstNm === ''
    ) {
      setMessage('사업명이 등록되지 않았습니다.');
      valid = false;
    }
    // if (
    //   chance.partnerInc === null ||
    //   chance.partnerInc === undefined ||
    //   chance.partnerInc === ''
    // ) {
    //   setMessage('고객명이 등록되지 않았습니다.');
    //   valid = false;
    // }
    // if (
    //   chance.chargeNm === null ||
    //   chance.chargeNm === undefined ||
    //   chance.chargeNm === ''
    // ) {
    //   setMessage('고객 담당자명이 등록되지 않았습니다.');
    //   valid = false;
    // }
    // if (
    //   chance.chanceTyp === null ||
    //   chance.chanceTyp === undefined ||
    //   chance.chanceTyp === ''
    // ) {
    //   setMessage('기회구분이 등록되지 않았습니다.');
    //   valid = false;
    // }
    if (
      chance.prjImpt === null ||
      chance.prjImpt === undefined ||
      chance.prjImpt === ''
    ) {
      setMessage('프로젝트 중요도가 등록되지 않았습니다.');
      valid = false;
    }
    if (
      chance.bugetYn === null ||
      chance.bugetYn === undefined ||
      chance.bugetYn === ''
    ) {
      setMessage('예산여부가 등록되지 않았습니다.');
      valid = false;
    }
    // if (
    //   chance.partnerTyp === null ||
    //   chance.partnerTyp === undefined ||
    //   chance.partnerTyp === ''
    // ) {
    //   setMessage('거래처유형이 등록되지 않았습니다.');
    //   valid = false;
    // }
    return valid;
  };
  const checkValid2 = (costYnValue) => {
    let valid = checkValid1();

    // if (
    //   chance.incTyp === null ||
    //   chance.incTyp === undefined ||
    //   chance.incTyp === ''
    // ) {
    //   setMessage('거래처 유형이 등록되지 않았습니다.');
    //   valid = false;
    // }

    // if (
    //   chance.solutionTyp === null ||
    //   chance.solutionTyp === undefined ||
    //   chance.solutionTyp === ''
    // ) {
    //   setMessage('아이템 구분이 등록되지 않았습니다.');
    //   valid = false;
    // }

    if (
      chance.productYn === null ||
      chance.productYn === undefined ||
      chance.productYn === ''
    ) {
      setMessage('제품 여부가 등록되지 않았습니다.');
      valid = false;
    }

    if (
      chance.vendorCmp === null ||
      chance.vendorCmp === undefined ||
      chance.vendorCmp === ''
    ) {
      setMessage('매출처가 등록되지 않았습니다.');
      valid = false;
    }

    // if (
    //   chance.vendorEmp === null ||
    //   chance.vendorEmp === undefined ||
    //   chance.vendorEmp === ''
    // ) {
    //   setMessage('매출처 담당자가 등록되지 않았습니다.');
    //   valid = false;
    // }

    if (
      chance.expBenefitRate === null ||
      chance.expBenefitRate === undefined ||
      chance.expBenefitRate === ''
    ) {
      setMessage('원가산출이 완료되지 않았습니다');
      valid = false;
    }

    if (
      chance.expSales === null ||
      chance.expSales === undefined ||
      chance.expSales === ''
    ) {
      setMessage('원가산출이 완료되지 않았습니다');
      valid = false;
    }

    if (
      chance.expBuy === null ||
      chance.expBuy === undefined ||
      chance.expBuy === ''
    ) {
      setMessage('원가산출이 완료되지 않았습니다');
      valid = false;
    }

    //isAuth가 true이면 skip => "NO_PERPROMANCE" 권한자는 true
    if (!isAuth) {
      // isAuth가 true일떄는 계산안함.
      // if (chance?.expBenefitRate === '0.00') {
      //   setMessage('예상이익률이 0.00입니다. 재확인하세요.');
      //   valid = false;
      // }
      const numberExpRate = Number(chance?.expBenefitRate);
      if (numberExpRate <= 0) {
        setMessage('예상이익률이 0 이하입니다. 재확인하세요.');
        valid = false;
      }
    }

    if (
      chance.expDay === null ||
      chance.expDay === undefined ||
      chance.expDay === ''
    ) {
      setMessage('매출 예상일이 등록되지 않았습니다');
      valid = false;
    }

    if (costYnValue !== 'Y') {
      setMessage('원가산출이 확정되지 않았습니다.');
      valid = false;
    }

    return valid;
  };
  const checkValid3 = (costYnValue) => {
    let valid = checkValid1() && checkValid2(costYnValue);
    // if (valid) {
    //   if (
    //     chance.mtnFreeSt === null ||
    //     chance.mtnFreeSt === undefined ||
    //     chance.mtnFreeSt === ''
    //   ) {
    //     setMessage('무상 유지 보수 시작일이 등록되지 않았습니다');
    //     valid = false;
    //   }

    //   if (
    //     chance.mtnFreeEd === null ||
    //     chance.mtnFreeEd === undefined ||
    //     chance.mtnFreeEd === ''
    //   ) {
    //     setMessage('무상 유지 보수 종료일이 등록되지 않았습니다');
    //     valid = false;
    //   }
    // }
    return valid;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} sx={{ height: '200px', my: 1 }}>
      {/* <Grid container spacing={2} sx={{ height: '400px', my: 1 }}> */}
        {/* <Grid item xs={5}>
          <ChanceStepInfoContainer />
        </Grid> */}
        {/* <Grid item xs={7}>
          <ChanceGuideContainer />
        </Grid> */}
        <Grid item sx={12}>
          <ChanceStepInfoContainer />
        </Grid>
      </Grid>

      <div ref={stepperRef}></div>
      <Stepper
        sx={{
          pt: 2,
          pb: 1,
          backgroundColor: '#eeeeee',
        }}
        activeStep={activeStep}
        alternativeLabel
      >
        {steps.map((step) => {
          const stepProps = {};
          const labelProps = {};

          if (activeStep === steps.length - 1) {
            stepProps.completed = true;
          }
          return (
            <Step key={step.label} {...stepProps}>
              <StepLabel
                {...labelProps}
                // StepIconProps={{
                //   style: {
                //     color: '#424242'
                //   }
                // }}
              >
                {step.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          py: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >

        </Box>

        <Box sx={{ flex: '1 1 auto' }} />

        {canCopy && (
          <Box sx={{ mx: 1 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => setCopyVisible(true)}
              >
               기회 복사
              </Button>
            </Box>
        )}

        {canFail && (
          <Box sx={{ mr: 1 }}>
            <Button
              variant="contained"
              color="default"
              onClick={() => setFailVisible(true)}
            >
              기회 실패
            </Button>
          </Box>
        )}

        {canDelete && (
          <Box>
            <Button
              variant="contained"
              color="error"
              onClick={() => setRemoveVisible(true)}
            >
              기회 삭제
            </Button>
          </Box>
        )}
          
        <Box sx={{ mr: 4 }}>
        </Box>

        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            color={steps[activeStep + 1].color}
            onClick={handleNext}
          >
            {steps[activeStep + 1].label}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        {activeStep === 0 && (
          <ChanceConfirmContainer
            activeTap={activeTap}
            handleTabChange={handleTabChange}
            stepperRef={stepperRef}
          />
        )}
        {activeStep === 1 && (
          <ChanceNeedsAnanlysisContainer
            activeTap={activeTap}
            handleTabChange={handleTabChange}
            stepperRef={stepperRef}
          />
        )}
        {activeStep === 2 && (
          <ChanceEstimateContainer
            activeTap={activeTap}
            handleTabChange={handleTabChange}
            stepperRef={stepperRef}
          />
        )}
        {activeStep === 3 && (
          <ChanceOrderContainer
            activeTap={activeTap}
            handleTabChange={handleTabChange}
            stepperRef={stepperRef}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          py: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          {canDelete && (
            <Button
              variant="contained"
              color="error"
              onClick={() => setRemoveVisible(true)}
            >
              삭제
            </Button>
          )}
        </Box>

        <Box sx={{ flex: '1 1 auto' }} />
        {canFail && (
          <Box sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="default"
              onClick={() => setFailVisible(true)}
            >
              수주 실패
            </Button>
          </Box>
        )}
        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            color={steps[activeStep + 1].color}
            onClick={handleNext}
          >
            {steps[activeStep + 1].label}
          </Button>
        )}
      </Box>

      <AskDialog
        visible={visible}
        title="다음단계 이동"
        description={message}
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
      <ChanceFailModal
        visible={failVisible}
        title="경고 : 프로젝트 수주 실패"
        description="더이상 사업 진행이 되지 않습니다."
        onConfirm={onFailConfirm}
        onCancle={onFailCancle}
      />
      <AskDialog
        visible={copyVisible}
        title="프로젝트 복사"
        description="해당 프로젝트를 복사하시겠습니까?"
        onConfirm={onCopyConfirm}
        onCancle={onCopyCancle}
      />
      <AskDialog
        visible={removeVisible}
        title="프로젝트 삭제"
        description="해당 프로젝트를 삭제하시겠습니까?(복구 불가)"
        onConfirm={onRemoveConfirm}
        onCancle={onRemoveCancle}
      />
    </Box>
  );
};

export default ChanceStepperContainer;
